import React, { useEffect, useState, useRef } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import ActionButton from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink';
import ButtonCollection from '../builder/ButtonCollection.js';
import CloseButton from '../builder/CloseButton';
import ImageUploader from '../builder/ImageUploader';
import Range from '../builder/Range';

import FloatingLayerPublic from './FloatingLayerPublic';

import SimpleFabric from '../canvas/SimpleFabric';
import LeveledHTMLText from './helpers/LeveledHTMLText.js';

const SingleItem = (props) => {
	const type = props?.type ?? 'sticker';
	const name = props?.name ?? false;
	const image = props?.image ?? false;
	const meta = props?.meta ?? false;
	const is_empty = props?.is_empty ?? false;
	const img_proportion = props?.img_proportion ?? '1x1';

	if(image === false) return null;

	const item_styles = {
		backgroundImage:`url(${image})`,
		backgroundSize:(is_empty) ? '30px' : null,
	}

	const getItemClasses = () => {
		let is_selected = false;
		if(is_empty && meta == '') is_selected = true;
		if(!is_empty && meta != '' && image.indexOf(meta) != -1) is_selected = true;
		if(type != 'overlay') is_selected = false;

		const item_classes = ['photo_editor_thumb', 'gui_with_alpha_bg'];
		if(!is_selected) item_classes.push('original_color_item');

		return item_classes.join(" ");
	};

	return (
		<>
			<div class={getItemClasses()}>
				<div style={item_styles} onClick={props.onClick}>
					<img src={`${window.cdn_url}img/blank_${img_proportion}.png`} width="100%" />
				</div>
			</div>

			{(name !== false) && (
				<div class="item_name">{name}</div>
			)}
		</>
	)
}

const ItemSelector = ({ items, promo, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const type = props?.type ?? 'overlay';
	
	const meta = props?.meta ?? {};
	
	const display_names = promo?.photo_editor_sticker_name ?? false;
	const mandatory_frame = promo?.photo_editor_mandatory_frame ?? false;
	const img_proportion = promo?.photo_editor_proportion ?? '1x1';	// w:h

	const is_light_layout = ((promo?.config?.layout?.text_color ?? 'wt') == 'wt') ? true : false;

	const classes = ['gui_img_selector', ((type == 'sticker') ? 'stickers' : 'frames')];
	if(display_names) classes.push('with_names');

	const addItem = (item) => {
		if(props.addItem == undefined) return;

		props.addItem(type, {
			url:item.full,
			name:item.name,
			image:item.image,
		});
	};

	const removeItem = () => {
		if(props.removeItem == undefined) return;

		props.removeItem();
	};

	return (
		<ul class={classes.join(" ")} style={{width:'100%'}}>
			{(type == 'overlay' && !mandatory_frame) && (
				<li>
					<SingleItem
						type={type}
						image={`/img/photo_editor_svg/delete_${(is_light_layout) ? 'bk' : 'wt'}.svg`}
						name={(display_names) ? i18n("Empty") : null}
						onClick={removeItem}
						meta={meta}
						is_empty={true}
						img_proportion={img_proportion}
					/>
				</li>
			)}

			{items.map((item, i) => {
				return (
					<li key={i}>
						<SingleItem
							type={type}
							image={item.full}
							name={(display_names) ? item.name : null}
							onClick={() => addItem(item)}
							meta={meta}
							is_empty={false}
							img_proportion={img_proportion}
						/>
					</li>
				)
			})}
		</ul>
	)
};

const StickerSelector = (props) => {
	const type = props?.type ?? 'sticker';
	const is_mobile = props?.is_mobile ?? false;
	const validation = props?.validation ?? {};

	const min_items = props?.promo?.photo_editor_num_stickers_min ?? 0;
	const max_items = props?.promo?.photo_editor_num_stickers_max ?? false;
	const display_names = props?.promo?.photo_editor_sticker_name ?? false;

	const classes = ['gui_img_selector', ((type == 'sticker') ? 'stickers' : 'frames')];
	if(display_names) classes.push('with_names');

	const list_classes = ['stickers_list', 'gui_overflowed_auto'];
	if(!is_mobile && min_items > 0 && validation !== true) list_classes.push('with_message');
	if(validation == 'max') list_classes.push('gui_withoutEvents');

	return (
		<div class="gui_pos_rel" style={{ height:'100%', width:'100%' }}>
			{(!is_mobile && min_items > 0 && validation !== true) && (
				<span id="warning_min_stickers" class={((validation != 'min') ? 'greenBg' : 'redBg')}>
					{(min_items != max_items) ? (
						<FormattedMessage
							id="You have to select, at least, {min_stickers} stickers"
							values={{ min_stickers:min_items }}
						/>
					) : (
						<FormattedMessage
							id="You have to select {stickers} stickers"
							values={{ stickers:min_items }}
						/>
					)}
				</span>
			)}

			<div class={list_classes.join(" ")}>
				<ItemSelector
					{...props}
				/>
			</div>

			{(validation == 'max') && (
				<div id="max_stickers_error_msg" class="not_in_top">
					<FormattedMessage
						id="The maximum stickers is {max}"
						values={{ max:max_items }}
					/>
				</div>
			)}
		</div>
	)
};

const UserPhotoEditor = (props) => {
	useCSS('comp/photoeditor.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promo = props?.promotion.Promotion ?? false;
	const builder = props?.builder ?? false;
	const is_mobile = props?.is_mobile ?? false;
	const stickers = promo?.stickers ?? [];
	const marks = promo?.marks ?? [];

	const already = useRef(false);
	const cnv = useRef(null);

	const default_meta = {
		marks:'',
		stickers:{},
	};

	const full_photo_editor_custom = (isNotEmpty(promo, 'full_photo_editor_custom')) ? promo.full_photo_editor_custom : false;

	const [ display, setDisplay ] = useState((promo.promotion_type_id == '20') ? (((promo?.marks ?? []).length > 0) ? 'frames' : 'stickers') : ((full_photo_editor_custom === false) ? 'photo' : 'stickers'));
	const [ display_confirm_delete, setDisplayConfirmDelete ] = useState(false);
	const [ display_editor, setDisplayEditor ] = useState(false);
	const [ exported_image, setExportedImage ] = useState(props?.exported_image ?? false);
	const [ display_mobile_options, setDisplayMobileOptions ] = useState(false);
	const [ meta, setMeta ] = useState(default_meta);
	const [ minzoom, setMinZoom ] = useState(1);
	const [ maxzoom, setMaxZoom ] = useState(100);
	const [ rotation, setRotation ] = useState(0);
	const [ image, setImage ] = useState(null);
	const [ uploaded_image, setUploadedImage ] = useState(false);
	const [ validate_elements, setValidateElements ] = useState({});
	const [ zoom, setZoom ] = useState(100);
	const [ zoomed, setZoomed ] = useState(false);
	const [ lock, setLock ] = useState(false);

	useEffect(() => {
		validateElements();

		if((props.pm?.photo_editor_automatic ?? false) && !already.current) {
			already.current = true;

			if(props.pm.promotion_type_id == '20' && props.exported_image == undefined) {
				open();
			}
		}
	}, []);

	if(!promo) return null;

	const toggleItemSelected = (item) => {
		// if we don't have any element selected, we select the main_image:
		if(item == undefined) {
			item = cnv.current.getObjectByID('main_image');
			return;
		}

		if(item.type == "main_image") {
			setZoom(Math.ceil(item.scaleX * 100));
			setLock(item.lockScalingX);
			if(!is_mobile) setDisplay('photo');
		}

		if(item.type == "sticker") {
			setDisplay('stickers');
		}
	};

	const zoomPhoto = (k, v) => {
		cnv.current.scaleSelectedItem(parseInt(v));
		setZoom(parseInt(v));
	};

	const rotatePhoto = (k, v) => {
		cnv.current.rotateSelectedItem(parseInt(v));
		setRotation(parseInt(v));
	};

	const deleteSelected = () => {
		setDisplayConfirmDelete(true);
	};

	const confirmDeleteSelected = () => {
		cnv.current.resetCanvas();
		cnv.current = undefined;

		setExportedImage(false);
		setDisplayConfirmDelete(false);
		setDisplayEditor(false);
		setUploadedImage(false);
		setMeta(default_meta);
		restoreScrollEvents();
	}

	const addImage = (k, v) => {
		preventScrollEvents();
		setDisplayEditor(true);

		setTimeout(() => {
			if(k == "image") cnv.current.addB64(v, 'main_image', (item) => {
				validateElements();
				setUploadedImage(true);

				const minzoom = Math.ceil(item.scaleX * 100);
				setMinZoom(minzoom);
				setMaxZoom(minzoom + 100);

				addMandatory();
			});
		}, 100);
	};

	const changeDisplayView = (view) => {
		cnv.current.deselectElements();

		setDisplay(view);
		setDisplayMobileOptions(true);
	};

	const unselectCanvasElement = (e) => {
		if(e.target.nodeName != 'CANVAS' && e.target.nodeName != 'A') {
			cnv.current.deselectElements();
		}
	};

	const submit = () => {
		//const exported = cnv.current.exportToJpeg();

		if(props.onFinish !== undefined) props.onFinish(exported_image, meta);
	}

	const finish = () => {
		const exported = cnv.current.exportToJpeg();

		//if(props.onFinish !== undefined) props.onFinish(exported.b64, meta, exported.file);
		restoreScrollEvents();

		setDisplayEditor(false);
		setExportedImage(exported.b64);
	};

	const foldUpMenu = () => {
		setDisplayMobileOptions(false);
	};

	const addElement = (type, data) => {
		const _meta = { ...meta };

		if(type == 'sticker') {
			cnv.current.addImage('sticker', data);
			_meta.stickers[data.image] = (_meta.stickers[data.image] == undefined) ? 1 : _meta.stickers[data.image] + 1;
		}

		setMeta(_meta);

		if(type == 'overlay') {
			cnv.current.addOverlayImage('overlay', data);
			_meta.marks = data.image;
		}

		validateElements();

		foldUpMenu();
	};

	const onRemove = (object) => {
		if(object.id === 'sticker') {
			const _meta = { ...meta };
			_meta.stickers[object.text]--;
			if(_meta.stickers[object.text] == 0) delete _meta.stickers[object.text];
			setMeta(_meta);
		}

		validateElements();
	};

	const validateElements = () => {
		let stickers = 0;
		const v = {
			marks:true,
			stickers:true,
		};

		for(var i in meta.stickers) {
			stickers += meta.stickers[i];
		}

		if(promo.photo_editor_num_stickers_min != undefined && stickers < promo.photo_editor_num_stickers_min) {
			v.stickers = 'min';
		}

		if(promo.photo_editor_num_stickers_max != undefined && stickers >= promo.photo_editor_num_stickers_max) {
			v.stickers = 'max';
		}

		setValidateElements(v);
	};

	const removeOverlay = () => {
		cnv.current.removeOverlayImage();

		setMeta({ ...meta, marks:'' });
		foldUpMenu();
	};

	const addMandatory = () => {
		setTimeout(() => {
			if(full_photo_editor_custom !== false) {
				cnv.current.addBackgroundImage(promo.full_photo_editor_custom);
			}

			setUploadedImage(true);

			if(full_photo_editor_custom !== false && promo.promotion_type_id == "20") return;

			if(promo.photo_editor_mandatory_frame) {
				const mark = promo.marks[0];
				setTimeout(() => {
					addElement('overlay', { url:mark.full, name:mark.name, image:mark.image });
				}, 200);
			}
		}, 0);
	};

	const preventScrollEvents = () => {
		// this was the b**ch that broke the code when scrolling down (on mobile) when in register form. Remember this!
		window.scrollTo(0, 0);

		$('body').css({ overflow:'hidden' });
	};

	const restoreScrollEvents = () => {
		$('body').css({ overflow:'visible' });
	};

	const open = () => {
		preventScrollEvents();

		setTimeout(() => {
			setDisplayEditor(true);
			addMandatory();

			if(full_photo_editor_custom === false) return;
		}, 0);
	};

	const cancelUpload = () => {
		restoreScrollEvents();
		setUploadedImage(false);
		setDisplayEditor(false);
	};

	const toggleItemLocked = () => {
		cnv.current.toggleLock('main_image');
		setLock(!lock);
	};

	const img_proportion = promo?.photo_editor_proportion ?? '1x1';	// w:h

	const ressolution_factor = .5;
	let width = false;
	let height = false;
	switch(img_proportion) {
		case '120x63':
			// POST
			width = 1200 * ressolution_factor;
			height = 630 * ressolution_factor;
			break;

		case '375x667':
			// STORY
			width = 1127 * ressolution_factor;
			height = 2008 * ressolution_factor;
			break;

		case '1x1':
		default:
			// SQUARE
			width = 800 * ressolution_factor * 2;
			height = 800 * ressolution_factor * 2 ;
			break;
	}

	const button_label = (isNotEmpty(promo, 'photo_editor_label')) ? promo.photo_editor_label : ((!builder) ? ((promo.promotion_type_id != '20') ? "Upload and edit image" : "Click to start") : ((promo.promotion_type_id != '20') ? "Test your PhotoFun" : "Test your Scene"));

	const has_stickers = (stickers.length > 0) ? true : false;
	const has_frames = (marks.length > 0) ? true : false;
	if(!has_stickers && !has_frames) return null;

	const has_custom_img = (full_photo_editor_custom === false && promo.promotion_type_id != '20') ? true : false;
	const min_stickers = promo?.photo_editor_num_stickers_min ?? 0;
	const max_stickers = promo?.photo_editor_num_stickers_max ?? false;

	const label_photo = promo?.photo_editor_label_photo ?? "Photo";
	const label_stickers = promo?.photo_editor_label_stickers ?? i18n("Stickers");
	const label_marks = promo?.photo_editor_label_frames ?? "Frames";

	const scaling_mode = promo?.photo_editor_scaling_mode ?? '2';

	let min_stickers_msg = '';
	if(is_mobile && has_stickers && min_stickers > 0 && validate_elements.stickers !== true) {
		if(min_stickers != max_stickers) {
			min_stickers_msg = ` (${i18n("Min.")} ${min_stickers})`;
		} else {
			min_stickers_msg = ` (${i18n("Need")} ${min_stickers})`;
		}
	}

	const automatic_display = (promo.photo_editor_automatic != undefined && promo.photo_editor_automatic && !already.current) ? true : false;

	return (
		<div style={{width:((builder) ? '100%' : 'auto'), height:((builder) ? '100%' : 'auto')}}>
			{(!builder) ? (
				<div>
					{(automatic_display && !exported_image) && (
						<section class="gui_canvas_photo_editor gui_is_not_padded">
							<div class="gui_pos_rel" style={{width:'100vw', height:'100dvh', padding:'5vw'}}>
								<ImageUploader
									name="image"
									_class={{ first:true, other:'gui_not_bordered' }}
									image={image}
									change={addImage}
									_size={{ width, height }}
									_thumb_size={{ width:'calc(90vw - 6px)', height:'calc(100dvh - 10vw - 6px)' }}
									small_input={false}
									enable_cancel={false}
								/>
							</div>
						</section>
					)}

					{(!exported_image) && (
						<div class="input field required field_UserMedia">
							<label>{promo.need_photo}</label>

							{(!automatic_display) && (
								<div>
									{(full_photo_editor_custom === false && promo.promotion_type_id != '20') && (
										<ImageUploader
											only_element={true}
											element={
												<div class="gui_simulated_input">
													<ActionButton
														color="transparent"
													>{button_label}</ActionButton>
												</div>}
											name="image"
											image={image}
											change={addImage}
											_size={{width:width, height:height}}
											_thumb_size={{width:'100%', height:'100%'}}
											enable_cancel={((!builder) ? cancelUpload : null)}
										/>
									)}

									{(full_photo_editor_custom === false || promo.promotion_type_id == '20')  && (
										<div class="gui_simulated_input">
											<ActionButton
												color="transparent"
												onClick={open}
											>{button_label}</ActionButton>
										</div>
									)}
								</div>
							)}
						</div>
					)}

					{(zoomed) && (
						<FloatingLayerPublic
							pm={promo}
							backButton={() => setZoomed(false)}
						>
							<img src={exported_image} width="100%" />
						</FloatingLayerPublic>
					)}

					{(exported_image) && (
						<div class="input field required field_UserMedia">
							<label>{promo.need_photo}</label>
							<div class="gui_instagram_gallery is_selected_media">
								<ul class="gui_ig_gallery">
									<li id="gui_selected_media" style={{ margin:0, backgroundImage:`url(${exported_image})` }}>
										<img src={`${window.cdn_url}img/blank_${img_proportion}.png`} width="100%" />

										<span class="selected_tick ep-ok" />
									</li>
								</ul>

								<div class="gui_link_actions">
									<ActionLink
										onClick={() => setZoomed(true)}
									>***View</ActionLink>

									<ActionLink
										onClick={deleteSelected}
									>***Remove</ActionLink>

									<ActionLink
										onClick={submit}
									>Enviar</ActionLink>
										
								</div>
							</div>
						</div>
					)}
				</div>
			) : (
				<div class="gui_flexbox_auto gui_flex_col gui_flex_center gui_flex_align_center" style={{ width:'100%', height:'100%' }}>
					{(!exported_image) && (
						<div class="input field required field_UserMedia">
							{(full_photo_editor_custom === false && promo.promotion_type_id != '20') && (
								<ImageUploader
									only_element={true}
									element={
										<div class="gui_simulated_input">
											<ActionButton
												color="transparent"
											>{button_label}</ActionButton>
										</div>}
									name="image"
									image={image}
									change={addImage}
									_size={{ width, height }}
									_thumb_size={{ width:'100%', height:'100%' }}
									enable_cancel={((!builder) ? cancelUpload : null)}
								/>
							)}

							{(full_photo_editor_custom !== false || promo.promotion_type_id == '20') && (
								<div class="gui_simulated_input">
									<ActionButton
										color="transparent"
										onClick={open}
									>{button_label}</ActionButton>
								</div>
							)}
						</div>
					)}

					{(exported_image) && (
						<div
							style={{
								width:'500px',
								height:'500px',
								backgroundImage:`url(${exported_image})`,
								backgroundSize:'contain',
								backgroundRepeat:'no-repeat',
							}}
						>
							<img src={`${window.cdn_url}img/blank_${img_proportion}.png`} width="100%" />
						</div>
					)}
				</div>
			)}

			{(display_editor) && (
				<section class={'gui_canvas_photo_editor' + ((builder) ? ' in_builder' : '')} onClick={(e) => unselectCanvasElement(e)}>
					<div class={'canvas_container' + ((display_mobile_options) ? ' with_options_visible' : '')}>
						<div class={'gui_canvas_cont' + ((!uploaded_image) ? ' gui_pos_rel' : '')}>
							<div>
								<SimpleFabric
									ref={(node) => { cnv.current = node; }}
									fabricSelected={toggleItemSelected}
									fabricUnselected={toggleItemSelected}
									scalling_mode={scaling_mode}
									margins={false}
									onRemove={onRemove}
									_width={width}
									_height={height}
								/>
							</div>

							{(!uploaded_image) && (
								<ImageUploader
									only_element={true}
									element="true"
									_class={{first:true, other:'fit_to_parent'}}
									name="image"
									image={image}
									change={addImage}
									_size={{width:width, height:height}}
									_thumb_size={{width:'100%', height:'100%'}}
									enable_cancel={((!builder) ? cancelUpload : null)}
								/>
							)}
						</div>
					</div>

					{(uploaded_image) && (
						<div class="photo_editor_controls solid_buttons">
							<nav class="photo_editor_actions_nav">
								{(has_custom_img && !is_mobile) && (
									<ActionButton
										color='black'
										icon='knk-picture'
										_class={{ other:'whiteIcon original_color_item photo_tab'+((display == 'photo') ? ' selected' : '') }}
										onClick={() => changeDisplayView('photo')}
									>{label_photo}</ActionButton>
								)}

								{(has_custom_img && is_mobile) && (
									<ActionButton
										color={(lock) ? 'black' : 'gray'}
										icon={(lock) ? 'knk-zipped' : 'knk-unzipped'}
										_class={{ other:'whiteIcon original_color_item'+((display == 'photo') ? ' selected' : '') }}
										onClick={toggleItemLocked}
									>{label_photo}</ActionButton>
								)}

								{(has_frames) && (
									<ActionButton
										color='black'
										icon='knk-frame'
										_class={{ other:'whiteIcon original_color_item frames_tab'+((display == 'frames') ? ' selected' : '') }}
										onClick={() => changeDisplayView('frames')}
									>{label_marks}</ActionButton>
								)}

								{(has_stickers) && (
									<ActionButton
										color='black'
										icon='knk-sticker'
										_class={{ other:'whiteIcon original_color_item stickers_tab'+((display == 'stickers') ? ' selected' : '') }}
										onClick={() => changeDisplayView('stickers')}
										autotranslate={false}
									>{label_stickers + min_stickers_msg}</ActionButton>
								)}
							</nav>

							<article class={'selectors'+((display_mobile_options) ? ' visible' : '')}>
								{(display == 'photo') && (
									<div class="main_image_settings">
										<Range
											_class={{ first:true }}
											type="zoom"
											min={minzoom}
											max={maxzoom}
											change={zoomPhoto}
											name="img_zoom"
											value={zoom}
											default={100}
										/>

										<Range
											_class={{ first:true }}
											type="rotate"
											min={-180}
											max={180}
											change={rotatePhoto}
											name="img_rotation"
											value={rotation}
											default={0}
										/>

										<ActionButton
											color='transparent'
											icon={(lock) ? 'knk-zipped' : 'knk-unzipped'}
											_class={{other:'lock_button'}}
											onClick={toggleItemLocked}
										>{(lock) ? "Unlock" : "Lock"}</ActionButton>
									</div>
								)}

								{(has_stickers && display == 'stickers') && (
									<StickerSelector
										items={stickers}
										type='sticker'
										addItem={addElement}
										is_mobile={is_mobile}
										meta={meta.stickers}
										validation={validate_elements[display]}
										promo={promo}
										intl={props.intl}
									/>
								)}

								{(has_frames && display == 'frames') && (
									<ItemSelector
										items={marks}
										type='overlay'
										addItem={addElement}
										removeItem={removeOverlay}
										is_mobile={is_mobile}
										meta={meta.marks}
										promo={promo}
										intl={props.intl}
									/>
								)}

								<aside class="fold_up_menu original_color_item">
									<ActionButton
										color="transparent"
										_class={{ other:'blackColor' }}
										onClick={foldUpMenu}
									>Close</ActionButton>
								</aside>
							</article>

							<ButtonCollection
								id="photo_editor_header"
								type='nav'
								_class={{ other:'photo_editor_actions_nav flex_button' }}
							>
								<ActionButton
									color='red'
									_class={{ other:'original_color_item cancel_button' }}
									onClick={deleteSelected}
								>Cancel</ActionButton>

								<ActionButton
									disabled={validate_elements.stickers == 'min'}
									color='green'
									_class={{ other:'original_color_item accept_button' }}
									onClick={finish}
									autotranslate={false}
								>{i18n("Accept")}{((min_stickers > 0 && min_stickers < max_stickers) ? ` (${i18n("Min.")} ${min_stickers})` : '')}</ActionButton>
							</ButtonCollection>
						</div>
					)}

					{(builder) && (
						<CloseButton
							_class={{ other:'blackIcon' }}
							onClick={() => confirmDeleteSelected()}
						/>
					)}
				</section>
			)}

			{(display_confirm_delete) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("Confirmation of cancellation")}</h1>`}
					pm={promo}
					backButton={() => setDisplayConfirmDelete(false)}
					is_builder={builder}
				>
					<LeveledHTMLText>
						<p>{i18n("Are you sure you want to cancel this?")}</p>
						<p>{i18n("You will lose any changes that may be applied.")}</p>
					</LeveledHTMLText>

					<ButtonCollection id="photo_editor_header_confirm" _class={{other:'photo_editor_actions_nav gui_not_in_top_big flex_button'}}>
						<ActionButton
							color='red'
							_class={{ other:'original_color_item cancel_button' }}
							onClick={() => setDisplayConfirmDelete(false)}
						>No, keep working</ActionButton>

						<ActionButton
							color='green'
							_class={{ other:'original_color_item accept_button' }}
							onClick={() => confirmDeleteSelected()}
						>Yes, remove</ActionButton>
					</ButtonCollection>
				</FloatingLayerPublic>
			)}
		</div>
	)
};

export default injectIntl(UserPhotoEditor);